import React from 'react';

import {InaccessibleInfo, isInaccessibleInfo} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {GridCellSummary, GridType, StorableSummary} from 'constants/SampleConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {formatGridPosition} from 'samples/SampleUtil';
import StorableStore from 'samples/StorableStore';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';

interface Props {
  storableId: string;
  // Specified externally.
  className?: string;
  noLocationText: string;
  showGridCell: boolean;
  truncate?: boolean;
}

interface ConnectedProps {
  ancestralStorableIds?: Array<string> | null;
  storableOrInaccessibleInfoById: {[key: string]: StorableSummary | InaccessibleInfo} | null;
  gridCell: GridCellSummary | null;
}

export class AncestralStorablesConnector extends DataConnector<Props, ConnectedProps> {
  override stores = [StorableStore];

  enhanceProps(): ConnectedProps {
    StorableSummaryExtrasManager.requestFetch(this.props.storableId);
    const extras = this.storableStore().getStorableSummaryExtras();

    if (
      this.props.storableId &&
      extras.ancestralStorableIdsByStorableId &&
      extras.ancestralStorableIdsByStorableId[this.props.storableId] &&
      extras.gridCellsByContainerId
    ) {
      const ancestralStorableIds = extras.ancestralStorableIdsByStorableId[this.props.storableId];
      const storableOrInaccessibleInfoById = this.getOrFetchMultipleMaybeInaccessibleRequired(
        ManagedModelType.STORABLE_SUMMARY,
        ancestralStorableIds
      );
      return {
        ancestralStorableIds,
        storableOrInaccessibleInfoById,
        gridCell: extras.gridCellsByContainerId[this.props.storableId],
      };
    }
    return {ancestralStorableIds: null, storableOrInaccessibleInfoById: null, gridCell: null};
  }
}

export function AncestralStorables({
  ancestralStorableIds,
  storableOrInaccessibleInfoById,
  className,
  gridCell,
  noLocationText,
  showGridCell,
  truncate,
}: Props & ConnectedProps): JSX.Element | null {
  if (ancestralStorableIds == null || storableOrInaccessibleInfoById == null) {
    return null;
  }

  if (ancestralStorableIds.some((id) => isInaccessibleInfo(storableOrInaccessibleInfoById[id]))) {
    return <span className="u-faded">No access</span>;
  }
  const ancestralStorables = ancestralStorableIds.map((id) => storableOrInaccessibleInfoById[id]);
  if (noLocationText && ancestralStorables.length === 1) {
    return <span className="u-faded">{noLocationText}</span>;
  }

  const ancestorNames = ancestralStorables
    .slice(1)
    .reverse()
    .map(({name}) => name);
  const truncatedAncestorNames = ancestorNames.slice(ancestorNames.length - 2);

  // Hide all but the last 2 before adding grid cell (grid cell doesn't count towards truncation).
  if (gridCell != null && showGridCell) {
    const parentStorable = ancestralStorables[1];
    const gridType: GridType = APIUtil.isBox(parentStorable.id) ? GridType.BOX : GridType.PLATE;
    const gridPosition = formatGridPosition({
      gridType,
      ...gridCell,
      width: parentStorable.storableSchema.width,
    });
    ancestorNames.push(gridPosition);
    truncatedAncestorNames.push(gridPosition);
  }
  const ancestryString = ancestorNames.join(' / ');
  const truncated = truncate && ancestorNames.length > truncatedAncestorNames.length;

  if (truncated) {
    return (
      <Tooltip title={ancestryString} placement={TooltipPlacement.LEFT}>
        <span className="breadcrumbs-more" key="breadcrumbs">
          <BenchlingIcon icon="ellipsis" />
        </span>
        {' / '}
        <span className={className}>{truncatedAncestorNames.join(' / ')}</span>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title={ancestryString} placement={TooltipPlacement.LEFT}>
        <span className={className}>{ancestryString}</span>
      </Tooltip>
    );
  }
}

export default AncestralStorablesConnector.connect(AncestralStorables);
